/**
 * CNC Machining Time Section JavaScript
 * Handles animations and interactions for the CNC machining section
 */

class CNCMachiningSection {
  constructor() {
    this.sections = document.querySelectorAll('.cnc-machining-section');
    this.progressSections = document.querySelectorAll('.cnc-machining-time__section');
    
    this.stats = document.querySelectorAll('.cnc-machining__stat');
    this.progressItems = document.querySelectorAll('.cnc-machining-time__item');
    
    this.observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -50px 0px'
    };
    
    this.init();
  }
  
  init() {
    this.setupIntersectionObserver();
    this.addHoverEffects();
    this.setupProgressBarObserver();
    this.setupVideoPlayButton();
  }
  
  /**
   * Setup Intersection Observer for scroll animations
   */
  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('cnc-machining__stat--visible');
        }
      });
    }, this.observerOptions);
    
    this.stats.forEach(stat => {
      stat.classList.add('cnc-machining__stat--hidden');
      observer.observe(stat);
    });
  }

  /**
   * Setup Intersection Observer for progress bars
   */
  setupProgressBarObserver() {
    if (!('IntersectionObserver' in window)) return;
    
    const progressObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
          this.animateProgressBar(entry.target);
        }
      });
    }, this.observerOptions);
    
    this.progressItems.forEach(item => {
      progressObserver.observe(item);
    });
  }

  /**
   * Animate progress bar fill
   */
  animateProgressBar(item) {
    const progressFill = item.querySelector('.cnc-machining-time__progress-fill');
    if (!progressFill) return;
    
    const targetProgress = progressFill.getAttribute('data-progress');
    
    setTimeout(() => {
      progressFill.style.width = targetProgress + '%';
    }, 300);
  }
  
  
  /**
   * Add hover effects to stats
   */
  addHoverEffects() {
    this.stats.forEach(stat => {
      stat.addEventListener('mouseenter', () => {
        this.handleStatHover(stat, true);
      });
      
      stat.addEventListener('mouseleave', () => {
        this.handleStatHover(stat, false);
      });
    });
  }
  
  /**
   * Handle stat hover effects
   */
  handleStatHover(stat, isHovering) {
    const isDarkTheme = stat.closest('.cnc-machining-section--dark') !== null;
    
    if (isHovering) {
      stat.style.transform = 'translateY(-4px)';
      if (isDarkTheme) {
        stat.style.boxShadow = '0 12px 35px rgba(255, 255, 255, 0.15)';
      } else {
        stat.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.15)';
      }
    } else {
      stat.style.transform = 'translateY(0)';
      if (isDarkTheme) {
        stat.style.boxShadow = '0 8px 25px rgba(255, 255, 255, 0.1)';
      } else {
        stat.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
      }
    }
  }
  
  /**
   * Setup video play button functionality
   */
  setupVideoPlayButton() {
    const playButtons = document.querySelectorAll('.cnc-machining__play-button');
    playButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleVideoPlay(button);
      });
    });

    // Setup video event listeners
    const videos = document.querySelectorAll('.cnc-machining__video');
    videos.forEach(video => {
      this.setupVideoEventListeners(video);
    });

    // Pause hidden videos initially to避免桌面/移动端同时播放
    const containers = document.querySelectorAll('.cnc-machining__media-container');
    containers.forEach(container => {
      const vids = container.querySelectorAll('.cnc-machining__video');
      vids.forEach(v => {
        if (!this.isElementVisible(v)) {
          try { v.pause(); } catch (e) { /* noop */ }
        }
      });
    });
  }

  /**
   * Setup video event listeners
   */
  setupVideoEventListeners(video) {
    const overlay = video.parentElement.querySelector('.cnc-machining__video-overlay');
    const playButton = video.parentElement.querySelector('.cnc-machining__play-button');

    if (!overlay || !playButton) return;

    // 初始化状态类与可访问性文案（根据当前视频状态）
    const initPlaying = !video.paused && !video.ended;
    playButton.classList.toggle('cnc-machining__play-button--playing', initPlaying);
    playButton.setAttribute('aria-label', initPlaying ? 'Pause video' : 'Play video');
    playButton.setAttribute('title', initPlaying ? 'Pause' : 'Play');

    // Show overlay when video is paused
    video.addEventListener('pause', () => {
      overlay.style.opacity = '1';
      overlay.style.pointerEvents = 'auto';
      playButton.classList.remove('cnc-machining__play-button--playing');
      playButton.setAttribute('aria-label', 'Play video');
      playButton.setAttribute('title', 'Play');
    });

    // Hide overlay when video is playing
    video.addEventListener('play', () => {
      // 播放时不隐藏覆盖层，保持操作按钮可见，便于用户随时暂停
      overlay.style.opacity = '1';
      overlay.style.pointerEvents = 'auto';
      playButton.classList.add('cnc-machining__play-button--playing');
      playButton.setAttribute('aria-label', 'Pause video');
      playButton.setAttribute('title', 'Pause');
    });

    // Handle video end
    video.addEventListener('ended', () => {
      overlay.style.opacity = '1';
      overlay.style.pointerEvents = 'auto';
      playButton.classList.remove('cnc-machining__play-button--playing');
      playButton.setAttribute('aria-label', 'Play video');
      playButton.setAttribute('title', 'Play');
    });
  }

  /**
   * Utility: element visibility (display != none and in layout flow)
   */
  isElementVisible(el) {
    if (!el) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    if (el.offsetParent === null) {
      // Still consider visible if position: fixed
      const pos = style.position;
      if (pos !== 'fixed') return false;
    }
    return true;
  }

  /**
   * Get the video element that is currently visible within a container
   */
  getVisibleVideo(container) {
    if (!container) return null;
    const candidates = container.querySelectorAll('.cnc-machining__video');
    for (const v of candidates) {
      if (this.isElementVisible(v)) return v;
    }
    return candidates[0] || null;
  }
  
  /**
   * Handle video play action
   */
  handleVideoPlay(button) {
    const videoContainer = button.closest('.cnc-machining__media-container');
    const video = this.getVisibleVideo(videoContainer);
    
    if (!video) {
      console.log('No video element found');
      return;
    }

    if (video.paused) {
      video.play().catch(error => {
        console.error('Error playing video:', error);
      });
    } else {
      video.pause();
    }

    // Dispatch custom event
    const event = new CustomEvent('cnc-video-play', {
      detail: { 
        section: 'cnc-machining',
        action: video.paused ? 'pause' : 'play',
        video: video 
      }
    });
    document.dispatchEvent(event);
  }
  
  /**
   * Handle responsive behavior
   */
  handleResize() {
    const isMobile = window.innerWidth <= 768;
    const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    this.stats.forEach(stat => {
      if (isMobile || isReducedMotion) {
        stat.style.transform = 'none';
        stat.style.transition = isReducedMotion ? 'none' : 'box-shadow 0.3s ease';
      } else {
        stat.style.transition = 'transform 0.3s ease, box-shadow 0.3s ease';
      }
    });

    // 在断点切换时暂停隐藏视频，避免双播
    const containers = document.querySelectorAll('.cnc-machining__media-container');
    containers.forEach(container => {
      const vids = container.querySelectorAll('.cnc-machining__video');
      vids.forEach(v => {
        if (!this.isElementVisible(v)) {
          try { v.pause(); } catch (e) { /* noop */ }
        }
      });
    });
  }
}

// CSS for animation states
const animationCSS = `
  .cnc-machining__stat--hidden {
    opacity: 0;
    transform: translateY(30px);
    transition: opacity 0.6s ease, transform 0.6s ease;
  }
  
  .cnc-machining__stat--visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  .cnc-machining__stat--highlighted .cnc-machining__stat-value--gradient {
    animation: gradientShimmer 3s ease-in-out infinite;
  }
  
  @keyframes gradientShimmer {
    0%, 100% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
  }
`;

// Inject CSS
const style = document.createElement('style');
style.textContent = animationCSS;
document.head.appendChild(style);

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CNCMachiningSection();
  });
} else {
  new CNCMachiningSection();
}

// Handle resize events
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    const section = document.querySelector('.cnc-machining-section');
    if (section && section.cncMachiningInstance) {
      section.cncMachiningInstance.handleResize();
    }
  }, 250);
});

// Export for potential external use
window.CNCMachiningSection = CNCMachiningSection;